import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { InfoModal } from "@core/modal/InfoModal";
import { RubricGeneratorCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const AssessmentConfigStep: React.FC<
  StepComponentProps<RubricGeneratorCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [editingLevelIndex, setEditingLevelIndex] = useState<number | null>(
    null,
  );
  const [editingValue, setEditingValue] = useState("");

  const currentLevelCount = data.performanceLevelNames?.length || 4;
  const currentLevelDescriptions = data.performanceLevelNames || [];

  // Default level names based on count
  const getDefaultLevelNames = (count: number): string[] => {
    const names: Record<number, string[]> = {
      3: [
        t("rubric.performanceLevel.levelName.excellent", "Excellent"),
        t("rubric.performanceLevel.levelName.satisfactory", "Satisfactory"),
        t("rubric.performanceLevel.levelName.developing", "Developing"),
      ],
      4: [
        t("rubric.performanceLevel.levelName.outstanding", "Outstanding"),
        t("rubric.performanceLevel.levelName.satisfactory", "Satisfactory"),
        t("rubric.performanceLevel.levelName.developing", "Developing"),
        t("rubric.performanceLevel.levelName.inadequate", "Inadequate"),
      ],
      5: [
        t("rubric.performanceLevel.levelName.excellent", "Excellent"),
        t("rubric.performanceLevel.levelName.good", "Good"),
        t("rubric.performanceLevel.levelName.satisfactory", "Satisfactory"),
        t("rubric.performanceLevel.levelName.developing", "Developing"),
        t("rubric.performanceLevel.levelName.inadequate", "Inadequate"),
      ],
      6: [
        t("rubric.performanceLevel.levelName.outstanding", "Outstanding"),
        t("rubric.performanceLevel.levelName.excellent", "Excellent"),
        t("rubric.performanceLevel.levelName.proficient", "Proficient"),
        t("rubric.performanceLevel.levelName.satisfactory", "Satisfactory"),
        t("rubric.performanceLevel.levelName.developing", "Developing"),
        t("rubric.performanceLevel.levelName.inadequate", "Inadequate"),
      ],
      7: [
        t("rubric.performanceLevel.levelName.outstanding", "Outstanding"),
        t("rubric.performanceLevel.levelName.excellent", "Excellent"),
        t("rubric.performanceLevel.levelName.proficient", "Proficient"),
        t("rubric.performanceLevel.levelName.good", "Good"),
        t("rubric.performanceLevel.levelName.satisfactory", "Satisfactory"),
        t("rubric.performanceLevel.levelName.developing", "Developing"),
        t("rubric.performanceLevel.levelName.inadequate", "Inadequate"),
      ],
    };
    return names[count] || names[4];
  };

  // Initialize default performance level names when component mounts
  useEffect(() => {
    if (
      !data.performanceLevelNames ||
      data.performanceLevelNames.length === 0
    ) {
      const defaultNames = getDefaultLevelNames(4);
      onDataChange({
        performanceLevelNames: defaultNames,
        levels: 4,
      });
    }
  }, [data.performanceLevelNames, onDataChange]);

  const handleLevelCountChange = (newCount: number) => {
    const defaultNames = getDefaultLevelNames(newCount);
    onDataChange({
      performanceLevelNames: defaultNames,
      levels: newCount,
    });
  };

  const handleLevelNameEdit = (index: number, currentName: string) => {
    setEditingLevelIndex(index);
    setEditingValue(currentName);
  };

  const handleLevelNameSave = (index: number) => {
    const newDescriptions = [...currentLevelDescriptions];
    newDescriptions[index] = editingValue;
    onDataChange({ performanceLevelNames: newDescriptions });
    setEditingLevelIndex(null);
    setEditingValue("");
  };

  const handleLevelNameCancel = () => {
    setEditingLevelIndex(null);
    setEditingValue("");
  };

  const getLevelNames = (): string[] => {
    if (currentLevelDescriptions.length === currentLevelCount) {
      return currentLevelDescriptions;
    }
    return getDefaultLevelNames(currentLevelCount);
  };

  const infoExamples = [
    {
      emoji: "📊",
      title: t(
        "rubric.performanceLevel.examples.3level.title",
        "3-Level System",
      ),
      description: t(
        "rubric.performanceLevel.examples.3level.description",
        "For simple and clear evaluation: Excellent, Satisfactory, Developing",
      ),
    },
    {
      emoji: "⚖️",
      title: t(
        "rubric.performanceLevel.examples.4level.title",
        "4-Level System",
      ),
      description: t(
        "rubric.performanceLevel.examples.4level.description",
        "For balanced sensitivity: Outstanding, Satisfactory, Developing, Inadequate",
      ),
    },
    {
      emoji: "🎯",
      title: t(
        "rubric.performanceLevel.examples.5level.title",
        "5-Level System",
      ),
      description: t(
        "rubric.performanceLevel.examples.5level.description",
        "For detailed evaluation: Excellent, Good, Satisfactory, Developing, Inadequate",
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t(
            "rubric.performanceLevel.title",
            "Performance Level Configuration",
          )}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "rubric.performanceLevel.description",
            "Select the right performance level count. More levels provide finer distinctions but may complicate consistent use. Consider your students' class level and assignment complexity.",
          )}
        </p>
      </div>

      {/* Info Box */}
      <div
        className={`bg-${themeColor}-50 dark:bg-${themeColor}-900/20 p-4 rounded-xl border border-${themeColor}-200 dark:border-${themeColor}-800`}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl">📊</span>
          <div className="flex-1">
            <h3
              className={`font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t(
                "rubric.performanceLevel.guide.title",
                "Teacher Guide: Performance Level Selection",
              )}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {t(
                "rubric.performanceLevel.guide.description",
                "Choose the most appropriate performance level count for your rubric. Each level count has its own advantages.",
              )}
            </p>
            <div className="space-y-2 text-sm">
              <p>
                <strong>3 {t("rubric.levels", "levels")}:</strong>{" "}
                {t(
                  "rubric.performanceLevel.guide.3levels",
                  "Simple and clear - great for elementary students or simple assignments",
                )}
              </p>
              <p>
                <strong>4 {t("rubric.levels", "levels")}:</strong>{" "}
                {t(
                  "rubric.performanceLevel.guide.4levels",
                  "Most popular choice - balanced sensitivity without complexity",
                )}
              </p>
              <p>
                <strong>5 {t("rubric.levels", "levels")}:</strong>{" "}
                {t(
                  "rubric.performanceLevel.guide.5levels",
                  "Detailed evaluation - ideal for high-risk or advanced level assignments",
                )}
              </p>
              <p>
                <strong>6-7 {t("rubric.levels", "levels")}:</strong>{" "}
                {t(
                  "rubric.performanceLevel.guide.6to7levels",
                  "Maximum sensitivity - for research projects or detailed skill evaluation",
                )}
              </p>
            </div>
            <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <p className="text-sm text-orange-700 dark:text-orange-300">
                <strong>{t("common.tip", "Tip")}:</strong>{" "}
                {t(
                  "rubric.performanceLevel.guide.tip",
                  "Start with 4 levels - you can always adjust names to your needs",
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowInfoModal(true)}
            className={`p-2 rounded-full bg-${themeColor}-100 dark:bg-${themeColor}-800 text-${themeColor}-600 dark:text-${themeColor}-400 hover:bg-${themeColor}-200 dark:hover:bg-${themeColor}-700 transition-colors`}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Performance Level Count Selection */}
      <div className="space-y-4">
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
        >
          {t("rubric.performanceLevel.levelCount", "Performance Level Count")}
        </h3>
        <div className="grid grid-cols-5 gap-3">
          {[3, 4, 5, 6, 7].map((count) => (
            <div
              key={count}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                currentLevelCount === count
                  ? `border-${themeColor}-500 bg-${themeColor}-500 text-white`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleLevelCountChange(count)}
            >
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-sm">{t("rubric.levels", "düzey")}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Level Names Customization */}
      <div className="space-y-4">
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
        >
          {t("rubric.performanceLevel.customize", "Customize Level Names")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("rubric.performanceLevel.clickToEdit", "Click to edit")}
        </p>
        <div className="grid grid-cols-1 gap-3">
          {getLevelNames().map((name, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div
                className={`w-8 h-8 rounded-full bg-${themeColor}-100 dark:bg-${themeColor}-900/50 flex items-center justify-center text-${themeColor}-600 dark:text-${themeColor}-400 font-bold`}
              >
                #{index + 1}
              </div>
              <div className="flex-1">
                {editingLevelIndex === index ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-800"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleLevelNameSave(index);
                        } else if (e.key === "Escape") {
                          handleLevelNameCancel();
                        }
                      }}
                      autoFocus
                    />
                    <button
                      onClick={() => handleLevelNameSave(index)}
                      className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleLevelNameCancel}
                      className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleLevelNameEdit(index, name)}
                    className="text-left font-medium hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {name}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Modal */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title={t(
          "rubric.performanceLevel.guide.title",
          "Teacher Guide: Performance Level Selection",
        )}
        description={t(
          "rubric.performanceLevel.guide.description",
          "Choose the most appropriate performance level count for your rubric. Each level count has its own advantages.",
        )}
        examples={infoExamples}
        themeColor={themeColor}
        icon="📊"
      />
    </div>
  );
};
